import { useId, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import type { LibraryCategory, LibraryConcept } from '../../api/centerstageApi';
import { ModalShell } from '../ModalShell';
import { CENTERSTAGE_COPY as COPY } from './centerstageCopy';
import type { CenterstageStyles } from './centerstageStyles';

/** The dialog's wording; the Centerstage page counts videos, the Question Bank picker counts questions. */
export interface ConceptsModalCopy {
  title: string;
  countsLoading: string;
  countsError: string;
  /** The accessible name of a disabled concept, e.g. "Bullying (no videos)". */
  noItems: (name: string) => string;
}

export const VIDEO_CONCEPTS_COPY: ConceptsModalCopy = {
  title: COPY.conceptsTitle,
  countsLoading: COPY.countsLoading,
  countsError: COPY.countsError,
  noItems: COPY.noVideosForConcept,
};

export interface CenterstageConceptsModalProps {
  styles: CenterstageStyles;
  isOpen: boolean;
  copy?: ConceptsModalCopy;
  categories: readonly LibraryCategory[];
  concepts: readonly LibraryConcept[];
  /** Public video counts by concept id; undefined while loading or after a failure. */
  counts: Readonly<Record<number, number>> | undefined;
  countsStatus: 'pending' | 'error' | 'success';
  /** Sets the concept filter and clears the search term; the page closes the dialog. */
  onPick: (conceptId: number) => void;
  onClose: () => void;
}

/**
 * Every library concept grouped by category, in the category row's order.
 * A concept with no public videos is disabled once the counts arrive; until
 * then (or if they fail) every concept can be picked.
 */
export function CenterstageConceptsModal({
  styles,
  isOpen,
  categories,
  concepts,
  counts,
  countsStatus,
  onPick,
  onClose,
  copy = VIDEO_CONCEPTS_COPY,
}: CenterstageConceptsModalProps) {
  const titleId = useId();
  const groups = useMemo(
    () => categories
      .map((category) => ({ category, concepts: concepts.filter((concept) => concept.categoryId === category.id) }))
      .filter((group) => group.concepts.length > 0),
    [categories, concepts],
  );

  return (
    <ModalShell isOpen={isOpen} onClose={onClose} ariaLabelledBy={titleId} presentation="admin" width="880px" centerOnContent>
      <div style={styles.dialogHeader}>
        <h2 id={titleId} style={styles.dialogTitle}>{copy.title}</h2>
        <button type="button" aria-label={COPY.closeDialog} onClick={onClose} style={styles.iconButton}>
          <FontAwesomeIcon icon={faXmark} aria-hidden="true" style={{ fontSize: '16px' }} />
        </button>
      </div>
      <div style={styles.dialogScroll}>
        <div style={styles.conceptGroups}>
          {countsStatus === 'pending' ? <p role="status" style={styles.countsNote}>{copy.countsLoading}</p> : null}
          {countsStatus === 'error' ? <p role="status" style={styles.countsNote}>{copy.countsError}</p> : null}
          {groups.map(({ category, concepts: members }) => {
            const headingId = `${titleId}-category-${category.id}`;
            return (
              <section key={category.id} aria-labelledby={headingId} style={styles.conceptGroup}>
                <div style={styles.conceptGroupHeader}>
                  <h3 id={headingId} style={styles.conceptGroupName}>{category.name}</h3>
                  <span style={styles.conceptGroupCount}>{COPY.conceptCount(members.length)}</span>
                </div>
                <ul style={styles.conceptColumns}>
                  {members.map((concept) => {
                    const empty = counts?.[concept.id] === 0;
                    return (
                      <li key={concept.id}>
                        <button
                          type="button"
                          disabled={empty}
                          aria-label={empty ? copy.noItems(concept.name) : undefined}
                          onClick={() => onPick(concept.id)}
                          style={{ ...styles.conceptButton, ...(empty ? styles.conceptButtonDisabled : {}) }}
                        >
                          {concept.name}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </section>
            );
          })}
        </div>
      </div>
    </ModalShell>
  );
}
